/**
 * Admin Planning Objects
 *
 * Central admin page for managing Cycles, Tracks, and Sessions:
 * create, rename, archive, and delete. This surface exists to handle
 * lifecycle operations that aren't surfaced in the operational planning views.
 *
 * Cascade policy (enforced here, not in stores):
 *   - Archiving a cycle cascade-archives its tracks.
 *   - Deleting a cycle/track requires choosing what happens to children:
 *       1. Archive all (recommended) — preserves governance history
 *       2. Leave in place — orphans children (explicitly labeled)
 *       3. Delete all — blocked whenever the cycle/track still has sessions
 *   - Sessions are never hard-deleted (migration 758): "delete" a session is
 *     a governed retire (canon 11 §2.1 Tier 1) — the row and everything
 *     under it stay, and it can be taken back from the session's page. A
 *     retired session still references its Track (`track_id` FK, ON DELETE
 *     RESTRICT), so a Track or Cycle that has ever had a session can no
 *     longer be permanently deleted through this page — "Delete all" is
 *     disabled once any session exists and the option is left as Archive.
 *
 * Route: /prioritization/settings/planning
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  Plus,
  Pencil,
  Archive,
  Trash2,
  ExternalLink,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  RotateCcw,
  AlertTriangle,
  ShieldAlert,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { createTrackWithFeedback } from '../../../lib/tracks/createTrackFlow';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { RetireSessionsDialog } from '../../../components/reversals/RetireSessionsDialog';
import type { DegapCycle } from '../../../types/prioritization/degapCycle';
import type { Track } from '../../../types/prioritization/track';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { toFriendlyDbError } from '../../../lib/friendlyDbError';
import { CreateCycleDialog } from '../../../components/prioritization/cycles/CreateCycleDialog';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const TABS = ['cycles', 'tracks', 'sessions'] as const;
type Tab = typeof TABS[number];
const TAB_LABELS: Record<Tab, string> = {
  cycles: 'Cycles',
  tracks: 'Tracks',
  sessions: 'Sessions',
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    draft: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
    paused: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    completed: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    archived: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    accepted: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    final_rejected: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border font-medium ${map[status] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function fmtDate(d: Date | string | undefined | null): string {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function sessionHasOutputs(session: PrioritizationSession): boolean {
  return !!(
    (session as unknown as { rankingOutput?: unknown }).rankingOutput ||
    (session as unknown as { resultingItemPoolId?: unknown }).resultingItemPoolId
  );
}

// ─── Inline name editor ───────────────────────────────────────────────────────

function InlineName({ value, onSave }: { value: string; onSave: (next: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) { setDraft(value); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [editing, value]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(trimmed); } finally { setSaving(false); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void commit(); if (e.key === 'Escape') setEditing(false); }}
          disabled={saving}
          className="text-sm border border-[var(--slate)] rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 w-48"
        />
        <button onClick={() => void commit()} disabled={saving} className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] disabled:opacity-40" title="Save"><Check className="h-3.5 w-3.5" /></button>
        <button onClick={() => setEditing(false)} disabled={saving} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]" title="Cancel"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] text-left"
    >
      {value}
      <Pencil className="h-3 w-3 text-[var(--text-ter)] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// ─── Cascade modal ────────────────────────────────────────────────────────────

type CascadeOption = 'archive-all' | 'leave-children' | 'delete-all';

interface CascadeContext {
  parentType: 'cycle' | 'track';
  parentId: string;
  parentName: string;
  trackCount: number;         // direct child tracks (cycles only)
  sessionCount: number;       // total sessions under this parent
  sessionsWithOutputs: number;
}

interface CascadeModalProps {
  ctx: CascadeContext;
  onCancel: () => void;
  onConfirm: (option: CascadeOption) => Promise<void>;
}

function CascadeModal({ ctx, onCancel, onConfirm }: CascadeModalProps) {
  const [selected, setSelected] = useState<CascadeOption>('archive-all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sessions are retired, not purged (migration 758) — the row (and its
  // track_id FK, ON DELETE RESTRICT) stays even after retiring, so a cycle
  // or track that has ever had a session can't be permanently deleted here.
  const deleteBlocked = ctx.sessionCount > 0;
  const childLabel = ctx.parentType === 'cycle'
    ? `${ctx.trackCount} track${ctx.trackCount !== 1 ? 's' : ''} and ${ctx.sessionCount} session${ctx.sessionCount !== 1 ? 's' : ''}`
    : `${ctx.sessionCount} session${ctx.sessionCount !== 1 ? 's' : ''}`;

  const options: Array<{
    id: CascadeOption;
    label: string;
    description: string;
    recommended?: boolean;
    destructive?: boolean;
    disabled?: boolean;
    disabledReason?: string;
  }> = [
    {
      id: 'archive-all',
      label: ctx.parentType === 'cycle'
        ? 'Archive cycle, Tracks, and sessions'
        : 'Archive Track and sessions',
      description: 'Preserves all history and governance artifacts. Reversible. Recommended.',
      recommended: true,
    },
    {
      id: 'leave-children',
      label: ctx.parentType === 'cycle'
        ? 'Leave Tracks and sessions in place'
        : 'Leave sessions in place',
      description: ctx.parentType === 'cycle'
        ? 'Removes only the cycle. Tracks become standalone; sessions remain under their tracks. Navigation links will break.'
        : 'Removes only the Track. Sessions become orphaned and will lose their planning context.',
    },
    {
      id: 'delete-all',
      label: ctx.parentType === 'cycle'
        ? 'Delete cycle, Tracks, and sessions permanently'
        : 'Delete Track and sessions permanently',
      description: deleteBlocked
        ? `Cannot delete: ${ctx.sessionCount} session${ctx.sessionCount !== 1 ? 's' : ''} would need to be retired first, and a retired session still belongs to its Track — this ${ctx.parentType} can't be permanently removed while any remain. Archive instead.`
        : 'Permanently removes everything. Cannot be undone.',
      destructive: true,
      disabled: deleteBlocked,
      disabledReason: deleteBlocked ? 'Sessions can only be retired, never deleted — archive instead.' : undefined,
    },
  ];

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(selected);
    } catch (e) {
      setError(e instanceof Error ? toFriendlyDbError(e.message, 'AdminPlanningObjects.CascadeModal') : 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-[var(--mustard-dim)]">
              <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[var(--text-pri)]">
                Remove {ctx.parentType === 'cycle' ? 'Cycle' : 'Track'}: "{ctx.parentName}"
              </h3>
              <p className="mt-0.5 text-sm text-[var(--text-ter)]">
                This {ctx.parentType} has {childLabel}. Choose what happens to them.
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="ml-2 shrink-0 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Governance warning */}
        {deleteBlocked && (
          <div className="mx-6 mt-4 flex items-start gap-2.5 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2.5">
            <ShieldAlert className="h-4 w-4 text-[var(--mustard)] mt-0.5 shrink-0" />
            <p className="text-xs text-[var(--mustard)]">
              <span className="font-semibold">{ctx.sessionCount} session{ctx.sessionCount !== 1 ? 's' : ''}</span> under this {ctx.parentType}
              {ctx.sessionsWithOutputs > 0 ? `, ${ctx.sessionsWithOutputs} with ranking outputs,` : ''} can only be retired, not deleted — their records stay.
              Permanent deletion is not available while any remain. Archive to retain history.
            </p>
          </div>
        )}

        {/* Options */}
        <div className="px-6 py-4 space-y-2.5">
          {options.map((opt) => {
            const isSelected = selected === opt.id;
            const isDisabled = opt.disabled;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && setSelected(opt.id)}
                className={[
                  'w-full text-left  border px-4 py-3 transition-colors',
                  isDisabled
                    ? 'border-[var(--rule)] bg-[var(--surface)] cursor-not-allowed opacity-50'
                    : isSelected
                      ? opt.destructive
                        ? 'border-[var(--coral)] bg-[var(--coral-dim)]'
                        : 'border-[var(--slate)] bg-[var(--slate-dim)]'
                      : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  {/* Radio dot */}
                  <div className={[
                    'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center',
                    isDisabled ? 'border-[var(--rule)]' :
                    isSelected
                      ? opt.destructive ? 'border-[var(--coral)] bg-[var(--coral)]' : 'border-[var(--slate)] bg-[var(--slate)]'
                      : 'border-[var(--rule)]',
                  ].join(' ')}>
                    {isSelected && !isDisabled && <div className="h-1.5 w-1.5 rounded-full bg-[var(--bg-raised)]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={[
                        'text-sm font-medium',
                        isDisabled ? 'text-[var(--text-ter)]' :
                        opt.destructive ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]',
                      ].join(' ')}>
                        {opt.label}
                      </p>
                      {opt.recommended && (
                        <span className="inline-flex items-center rounded-full bg-[var(--green-dim)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className={`mt-0.5 text-xs ${isDisabled ? 'text-[var(--text-ter)]' : opt.destructive ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                      {opt.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <p className="px-6 pb-2 text-xs text-[var(--coral)]">{error}</p>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={busy}
            className={[
              'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium  disabled:opacity-50 transition-colors',
              selected === 'delete-all'
                ? 'bg-[var(--coral)] text-white hover:bg-[var(--coral)]'
                : 'bg-[var(--slate)] text-white hover:bg-[var(--slate)]',
            ].join(' ')}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {selected === 'archive-all' ? 'Archive' : selected === 'delete-all' ? 'Delete permanently' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create modal ─────────────────────────────────────────────────────────────

function CreateModal({ title, onClose, onCreate }: {
  title: string;
  onClose: () => void;
  onCreate: (name: string, description: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try { await onCreate(name.trim(), description.trim()); onClose(); }
    catch (e) { setError(e instanceof Error ? toFriendlyDbError(e.message, 'AdminPlanningObjects.CreateModal') : 'Something went wrong'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Name <span className="text-[var(--coral)]">*</span></label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void submit(); if (e.key === 'Escape') onClose(); }}
              placeholder="Enter a name…"
              className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-[var(--slate)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional description…"
              className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-[var(--slate)] resize-none"
            />
          </div>
          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">Cancel</button>
          <button
            onClick={() => void submit()}
            disabled={!name.trim() || busy}
            className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cycles tab ───────────────────────────────────────────────────────────────

function CyclesTab() {
  const cycles = useDegapProcessStore((s) => s.cycles) as DegapCycle[];
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);
  const archiveCycle = useDegapProcessStore((s) => s.archiveCycle);
  const deleteCycle = useDegapProcessStore((s) => s.deleteCycle);

  const tracks = useTrackStore((s) => s.tracks) as Track[];
  const archiveTrack = useTrackStore((s) => s.archiveTrack);
  const deleteTrack = useTrackStore((s) => s.deleteTrack);

  const sessions = useSessionStore((s) => s.sessions) as PrioritizationSession[];
  const retireSession = useSessionStore((s) => s.retireSession);

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<string | null>(null);
  const [cascadeCtx, setCascadeCtx] = useState<CascadeContext | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { void fetchCycles(); }, [fetchCycles]);

  const filtered = useMemo(() => {
    let list = cycles;
    if (!showArchived) list = list.filter((c) => c.status !== 'archived');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [cycles, showArchived, search]);

  const buildCascadeCtx = useCallback((cycle: DegapCycle): CascadeContext => {
    const cycleTracks = tracks.filter((t) => t.cycleId === cycle.id);
    const trackIds = new Set(cycleTracks.map((t) => t.id));
    const cycleSessions = sessions.filter((s) => s.trackId && trackIds.has(s.trackId));
    const withOutputs = cycleSessions.filter(sessionHasOutputs).length;
    return {
      parentType: 'cycle',
      parentId: cycle.id,
      parentName: cycle.name,
      trackCount: cycleTracks.length,
      sessionCount: cycleSessions.length,
      sessionsWithOutputs: withOutputs,
    };
  }, [tracks, sessions]);

  const handleArchiveCycle = useCallback(async (id: string) => {
    setBusy(id);
    try { await archiveCycle(id); } finally { setBusy(null); setArchiveConfirm(null); }
  }, [archiveCycle]);

  const handleCascade = useCallback(async (option: CascadeOption) => {
    if (!cascadeCtx) return;
    const { parentId, parentName } = cascadeCtx;
    const cycleTracks = tracks.filter((t) => t.cycleId === parentId);
    const trackIds = new Set(cycleTracks.map((t) => t.id));
    const cycleSessions = sessions.filter((s) => s.trackId && trackIds.has(s.trackId));

    setBusy(parentId);
    try {
      if (option === 'archive-all') {
        // Archive sessions first (best-effort, no archive action — they stay visible under archived tracks)
        // Archive all tracks
        for (const track of cycleTracks) {
          if (track.status !== 'archived') await archiveTrack(track.id);
        }
        // Archive the cycle
        await archiveCycle(parentId);
      } else if (option === 'delete-all') {
        // Sessions are never hard-deleted (migration 758) — "delete" is a
        // governed retire. The modal disables this option once any session
        // exists (a retired session still references its Track, ON DELETE
        // RESTRICT, so the cycle can't be purged while one remains), but
        // retire defensively here too in case state changed between open
        // and confirm, and report refusals honestly rather than claiming
        // the cycle was removed.
        if (cycleSessions.length > 0) {
          const reason = `Cascade delete of cycle "${parentName}"`;
          const failures: string[] = [];
          for (const session of cycleSessions) {
            const result = await retireSession(session.id, reason);
            if (!result.ok) {
              const why = result.remedy ? `${result.error} — ${result.remedy}` : result.error;
              failures.push(`${session.name?.trim() || 'Untitled session'}: ${why}`);
            }
          }
          if (failures.length > 0) {
            throw new Error(`Could not retire ${failures.length} of ${cycleSessions.length} session(s), so the cycle was not deleted:\n${failures.join('\n')}`);
          }
          throw new Error(`${cycleSessions.length} session${cycleSessions.length !== 1 ? 's were' : ' was'} retired (not deleted — records stay and can be restored from each session's page). Because retired sessions still belong to their Track, the cycle can't be permanently deleted while any remain. Archive it instead.`);
        }
        // No sessions under this cycle — safe to hard-delete tracks and the cycle.
        for (const track of cycleTracks) {
          const ok = await deleteTrack(track.id);
          if (!ok) throw new Error(`Could not delete track "${track.name}".`);
        }
        const cycleResult = await deleteCycle(parentId);
        if (!cycleResult.success) throw new Error(cycleResult.error ?? 'Could not delete the cycle.');
      } else {
        // leave-children: only remove the cycle itself
        await deleteCycle(parentId);
      }
      // Only close the modal on a path that actually completed — a thrown
      // error above (a refusal or a "can't be deleted" stop) leaves it open
      // so CascadeModal's catch can show the message instead of it flashing
      // and disappearing.
      setCascadeCtx(null);
    } finally {
      setBusy(null);
    }
  }, [cascadeCtx, tracks, sessions, archiveCycle, archiveTrack, deleteCycle, deleteTrack, retireSession]);

  const archivedCount = cycles.filter((c) => c.status === 'archived').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cycles…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        {archivedCount > 0 && (
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            {showArchived ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
          </button>
        )}
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)]"
        >
          <Plus className="h-4 w-4" />
          New Cycle
        </button>
      </div>

      <div className="border border-[var(--rule)] overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--rule)]">
          <thead className="bg-[var(--surface)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Created</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">{search ? 'No cycles match your search.' : 'No cycles yet.'}</td></tr>
            )}
            {filtered.map((cycle) => (
              <tr key={cycle.id} className={`hover:bg-[var(--surface)] ${cycle.status === 'archived' ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3">
                  <InlineName value={cycle.name} onSave={(name) => updateCycle(cycle.id, { name })} />
                </td>
                <td className="px-4 py-3"><StatusBadge status={cycle.status as string} /></td>
                <td className="px-4 py-3 text-sm text-[var(--text-ter)]">{fmtDate(cycle.createdAt)}</td>
                <td className="px-4 py-3">
                  {archiveConfirm === cycle.id ? (
                    <span className="inline-flex items-center gap-2 text-xs">
                      <span className="text-[var(--text-sec)]">Archive this cycle?</span>
                      <button onClick={() => void handleArchiveCycle(cycle.id)} className="font-medium text-[var(--mustard)] hover:text-[var(--mustard)]">Archive</button>
                      <button onClick={() => setArchiveConfirm(null)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
                    </span>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/prioritization/cycles/${cycle.id}`}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] rounded"
                        title="View cycle"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                      {cycle.status !== 'archived' && (
                        <button
                          onClick={() => setArchiveConfirm(cycle.id)}
                          disabled={busy === cycle.id}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] rounded disabled:opacity-40"
                          title="Archive cycle"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {cycle.status === 'archived' && (
                        <button
                          onClick={() => void updateCycle(cycle.id, {})}
                          disabled={busy === cycle.id}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--green-dim)] rounded disabled:opacity-40"
                          title="Restore cycle"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setCascadeCtx(buildCascadeCtx(cycle))}
                        disabled={busy === cycle.id}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded disabled:opacity-40"
                        title="Remove cycle…"
                      >
                        {busy === cycle.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The one create-cycle form (owner ruling #7). */}
      <CreateCycleDialog open={creating} onClose={() => setCreating(false)} />

      {cascadeCtx && (
        <CascadeModal
          ctx={cascadeCtx}
          onCancel={() => setCascadeCtx(null)}
          onConfirm={handleCascade}
        />
      )}
    </div>
  );
}

// ─── Tracks tab ───────────────────────────────────────────────────────────────

function TracksTab() {
  const tracks = useTrackStore((s) => s.tracks) as Track[];
  const fetchTracks = useTrackStore((s) => s.fetchAllTracks);
  const updateTrack = useTrackStore((s) => s.updateTrack);
  const archiveTrack = useTrackStore((s) => s.archiveTrack);
  const deleteTrack = useTrackStore((s) => s.deleteTrack);
  const cycles = useDegapProcessStore((s) => s.cycles) as DegapCycle[];

  const sessions = useSessionStore((s) => s.sessions) as PrioritizationSession[];
  const retireSession = useSessionStore((s) => s.retireSession);

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<string | null>(null);
  const [cascadeCtx, setCascadeCtx] = useState<CascadeContext | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { void fetchTracks(); }, [fetchTracks]);

  const cycleMap = useMemo(() => new Map(cycles.map((c) => [c.id, c.name])), [cycles]);

  const filtered = useMemo(() => {
    let list = tracks;
    if (!showArchived) list = list.filter((t) => t.status !== 'archived');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tracks, showArchived, search]);

  const buildCascadeCtx = useCallback((track: Track): CascadeContext => {
    const trackSessions = sessions.filter((s) => s.trackId === track.id);
    const withOutputs = trackSessions.filter(sessionHasOutputs).length;
    return {
      parentType: 'track',
      parentId: track.id,
      parentName: track.name,
      trackCount: 0,
      sessionCount: trackSessions.length,
      sessionsWithOutputs: withOutputs,
    };
  }, [sessions]);

  const handleArchiveTrack = useCallback(async (id: string) => {
    setBusy(id);
    try { await archiveTrack(id); } finally { setBusy(null); setArchiveConfirm(null); }
  }, [archiveTrack]);

  const handleCascade = useCallback(async (option: CascadeOption) => {
    if (!cascadeCtx) return;
    const { parentId, parentName } = cascadeCtx;
    const trackSessions = sessions.filter((s) => s.trackId === parentId);

    setBusy(parentId);
    try {
      if (option === 'archive-all') {
        await archiveTrack(parentId);
        // Sessions stay — they're accessible but under an archived track
      } else if (option === 'delete-all') {
        // Sessions are never hard-deleted (migration 758) — "delete" is a
        // governed retire. The modal disables this option once any session
        // exists (a retired session still references its Track, ON DELETE
        // RESTRICT, so the track can't be purged while one remains), but
        // retire defensively here too in case state changed between open
        // and confirm, and report refusals honestly rather than claiming
        // the track was removed.
        if (trackSessions.length > 0) {
          const reason = `Cascade delete of Track "${parentName}"`;
          const failures: string[] = [];
          for (const session of trackSessions) {
            const result = await retireSession(session.id, reason);
            if (!result.ok) {
              const why = result.remedy ? `${result.error} — ${result.remedy}` : result.error;
              failures.push(`${session.name?.trim() || 'Untitled session'}: ${why}`);
            }
          }
          if (failures.length > 0) {
            throw new Error(`Could not retire ${failures.length} of ${trackSessions.length} session(s), so the Track was not deleted:\n${failures.join('\n')}`);
          }
          throw new Error(`${trackSessions.length} session${trackSessions.length !== 1 ? 's were' : ' was'} retired (not deleted — records stay and can be restored from each session's page). Because retired sessions still belong to this Track, it can't be permanently deleted while any remain. Archive it instead.`);
        }
        const ok = await deleteTrack(parentId);
        if (!ok) throw new Error('Could not delete the Track.');
      } else {
        // leave-children: remove only the track
        await deleteTrack(parentId);
      }
      setCascadeCtx(null);
    } finally {
      setBusy(null);
    }
  }, [cascadeCtx, sessions, archiveTrack, deleteTrack, retireSession]);

  const archivedCount = tracks.filter((t) => t.status === 'archived').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Tracks…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        {archivedCount > 0 && (
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            {showArchived ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
          </button>
        )}
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)]"
        >
          <Plus className="h-4 w-4" />
          New Track
        </button>
      </div>

      <div className="border border-[var(--rule)] overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--rule)]">
          <thead className="bg-[var(--surface)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Cycle</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Created</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">{search ? 'No Tracks match your search.' : 'No Tracks yet.'}</td></tr>
            )}
            {filtered.map((track) => (
              <tr key={track.id} className={`hover:bg-[var(--surface)] ${track.status === 'archived' ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3">
                  <InlineName value={track.name} onSave={(name) => updateTrack(track.id, { name })} />
                </td>
                <td className="px-4 py-3 text-sm text-[var(--text-ter)]">
                  {track.cycleId
                    ? <Link to={`/prioritization/cycles/${track.cycleId}`} className="hover:text-[var(--text-ter)] hover:underline">{cycleMap.get(track.cycleId) ?? track.cycleId.slice(0, 8) + '…'}</Link>
                    : <span className="text-[var(--text-ter)] italic">Standalone</span>}
                </td>
                <td className="px-4 py-3"><StatusBadge status={track.status} /></td>
                <td className="px-4 py-3 text-sm text-[var(--text-ter)]">{fmtDate(track.createdAt)}</td>
                <td className="px-4 py-3">
                  {archiveConfirm === track.id ? (
                    <span className="inline-flex items-center gap-2 text-xs">
                          <span className="text-[var(--text-sec)]">Archive this Track?</span>
                      <button onClick={() => void handleArchiveTrack(track.id)} className="font-medium text-[var(--mustard)] hover:text-[var(--mustard)]">Archive</button>
                      <button onClick={() => setArchiveConfirm(null)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
                    </span>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/prioritization/tracks/${track.id}`}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] rounded"
                        title="View Track"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                      {track.status !== 'archived' && (
                        <button
                          onClick={() => setArchiveConfirm(track.id)}
                          disabled={busy === track.id}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] rounded disabled:opacity-40"
                          title="Archive Track"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {track.status === 'archived' && (
                        <button
                          onClick={() => void updateTrack(track.id, {})}
                          disabled={busy === track.id}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--green-dim)] rounded disabled:opacity-40"
                          title="Restore Track"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setCascadeCtx(buildCascadeCtx(track))}
                        disabled={busy === track.id}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded disabled:opacity-40"
                        title="Remove Track…"
                      >
                        {busy === track.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateModal
          title="New Standalone Track"
          onClose={() => setCreating(false)}
          onCreate={async (name, description) => {
            const result = await createTrackWithFeedback({ name, description, isStandalone: true });
            if (!result.ok) throw new Error(result.error);
          }}
        />
      )}

      {cascadeCtx && (
        <CascadeModal
          ctx={cascadeCtx}
          onCancel={() => setCascadeCtx(null)}
          onConfirm={handleCascade}
        />
      )}
    </div>
  );
}

// ─── Sessions tab ─────────────────────────────────────────────────────────────

function SessionsTab() {
  const sessions = useSessionStore((s) => s.sessions) as PrioritizationSession[];
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);
  const updateSession = useSessionStore((s) => s.updateSession);
  const tracks = useTrackStore((s) => s.tracks) as Track[];

  const [search, setSearch] = useState('');
  const [retireTarget, setRetireTarget] = useState<PrioritizationSession | null>(null);

  useEffect(() => { void fetchSessions(); }, [fetchSessions]);

  const trackMap = useMemo(() => new Map(tracks.map((t) => [t.id, t.name])), [tracks]);

  const filtered = useMemo(() => {
    let list = sessions;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const da = a.scheduledFor ? new Date(a.scheduledFor).getTime() : 0;
      const db = b.scheduledFor ? new Date(b.scheduledFor).getTime() : 0;
      return db - da;
    });
  }, [sessions, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sessions…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <p className="text-xs text-[var(--text-ter)]">Create sessions from a Track's workspace.</p>
      </div>

      <div className="border border-[var(--rule)] overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--rule)]">
          <thead className="bg-[var(--surface)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Track</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Outputs</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Scheduled</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">{search ? 'No sessions match your search.' : 'No sessions yet.'}</td></tr>
            )}
            {filtered.map((session) => {
              const hasOutputs = sessionHasOutputs(session);
              return (
                <tr key={session.id} className="hover:bg-[var(--surface)]">
                  <td className="px-4 py-3">
                    <InlineName value={session.name} onSave={(name) => updateSession(session.id, { name })} />
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-ter)]">
                    {session.trackId
                      ? <Link to={`/prioritization/tracks/${session.trackId}`} className="hover:text-[var(--text-ter)] hover:underline">{trackMap.get(session.trackId) ?? session.trackId.slice(0, 8) + '…'}</Link>
                      : <span className="text-[var(--text-ter)] italic">Unlinked</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-[var(--text-ter)] capitalize">{session.sessionType?.replace(/_/g, ' ') ?? '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {hasOutputs
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]"><ShieldAlert className="h-3 w-3" />Has outputs</span>
                      : <span className="text-xs text-[var(--text-ter)]">None</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-ter)]">{fmtDate(session.scheduledFor)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/prioritization/sessions/${session.id}`}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] rounded"
                        title="View session"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        onClick={() => setRetireTarget(session)}
                        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                        title={hasOutputs ? 'Retire session (has outputs — use caution)' : 'Retire session'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {retireTarget && (
        <RetireSessionsDialog
          open={!!retireTarget}
          onClose={() => setRetireTarget(null)}
          sessions={[{ id: retireTarget.id, name: retireTarget.name }]}
        />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminPlanningObjects() {
  const [activeTab, setActiveTab] = useState<Tab>('cycles');
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const fetchTracks = useTrackStore((s) => s.fetchAllTracks);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);

  const refresh = useCallback(() => {
    void fetchCycles();
    void fetchTracks();
    void fetchSessions();
  }, [fetchCycles, fetchTracks, fetchSessions]);

  // Ensure all data is loaded for cascade checks
  useEffect(() => { void fetchCycles(); void fetchTracks(); void fetchSessions(); }, [fetchCycles, fetchTracks, fetchSessions]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Planning Objects"
        subtitle="Manage cycles, Tracks, and sessions — create, rename, archive, or remove."
        actions={
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            title="Refresh all"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        }
      />

      <div className="border-b border-[var(--rule)]">
        <nav className="flex gap-1 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[var(--slate)] text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:border-[var(--rule)]'
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'cycles' && <CyclesTab />}
      {activeTab === 'tracks' && <TracksTab />}
      {activeTab === 'sessions' && <SessionsTab />}
    </div>
  );
}
